import { isConvertedFromWorkspaceMode$ } from 'loadedState/loadedState$';
import { combineLatest, map } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';
import { dispatchAction } from '@ardoq/rxbeach';
import { IconName } from '@ardoq/icons';
import { SettingsType, type ButtonConfig } from '@ardoq/view-settings';
import { saveAsViewpointButtonState$ } from 'streams/saveAsViewpointButtonState$';
import { ViewSidePanelContentIds } from 'views/mainApp/viewSidePanel/types';
import { viewSidePanel$ } from 'views/mainApp/viewSidePanel/viewSidePanel$';
import { SidebarPanelContentIds } from 'canvas/sidebarPanel/types';
import { toggleSidebarPanel } from 'canvas/sidebarPanel/actions';
import { viewModifiers$ } from './viewModifiers$';
import { isRightEditorPaneOpen$ } from '../../isRightEditorPaneOpen$';
import { currentViewpointUserPermissions$ } from '../currentViewpointUserPermissions$';
import {
  hideViewSidePanel,
  showViewSidePanel,
} from 'views/mainApp/viewSidePanel/actions';
import { hideRightPane, showRightPane } from 'appContainer/actions';
import { GetContentOptionsType } from 'appModelStateEdit/legacyTypes';
import { trackOpenViewSettingsPanel } from './tracking';
import { noop } from 'lodash';
import { isViewEligibleForNewAssets } from 'views/environmentals/restrictedViews';
import { viewpointModeViews$ } from 'views/modeViews$';
import { featureOperations, Features } from '@ardoq/features';
import { presentationEditorToggle$ } from 'appContainer/presentationEditorToggle$';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { featureContext$ } from 'streams/featureContext$';
import { hasNewSidebarAndNavbar$ } from 'streams/hasNewSidebarAndNavbar$';
import { toSaveDropdownOptions } from 'appContainer/MainAppModule/toSaveDropdownOptions';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { getCanSaveViewpointAsWidget } from 'appContainer/MainAppModule/canSaveViewpointAsWidget';
import { isFileExporting$ } from 'appContainer/fileExports/fileExports';

const viewOptionsPanelRelatedContentIdsSet = new Set([
  ViewSidePanelContentIds.VIEW_OPTIONS,
  ViewSidePanelContentIds.PERSPECTIVES_GROUPS,
  ViewSidePanelContentIds.PERSPECTIVES_LABELS,
  ViewSidePanelContentIds.PERSPECTIVES_CONDITIONAL_FORMAT,
]);

const getIsViewOptionsSidePanelOpened = (
  viewSidePanelContentId: ViewSidePanelContentIds | null
) =>
  Boolean(
    viewSidePanelContentId &&
    viewOptionsPanelRelatedContentIdsSet.has(viewSidePanelContentId)
  );

export const rightControls$ = combineLatest([
  isRightEditorPaneOpen$,
  viewSidePanel$,
  viewModifiers$,
  currentViewpointUserPermissions$,
  presentationEditorToggle$,
  saveAsViewpointButtonState$,
  featureContext$,
  hasNewSidebarAndNavbar$,
  i18nLocale$,
  currentUserPermissionContext$,
  viewpointModeViews$,
  isConvertedFromWorkspaceMode$,
  isFileExporting$,
]).pipe(
  map(
    ([
      isRightEditorPaneOpen,
      { contentId: viewSidePanelContentId },
      { exportToPng, exportToSvg, addToDashboard, viewContext, mainViewId },
      { canCreateViewpoint, canUpdateViewpoint },
      presentationEditorToggle,
      saveAsViewpointButtonState,
      featureContext,
      hasNewSidebarAndNavbar,
      i18nLocale,
      permissionContext,
      viewpointModeViews,
      isConvertedFromWorkspaceMode,
      isFileExporting,
    ]) => {
      const { toggleLegend, isLegendActive } = viewContext;
      const canSaveViewpoint = canCreateViewpoint || canUpdateViewpoint;
      const isViewOptionsOpened = getIsViewOptionsSidePanelOpened(
        viewSidePanelContentId
      );
      const isSaveAsViewpointSidePanelOpened =
        viewSidePanelContentId === ViewSidePanelContentIds.SAVE_VIEWPOINT;

      const isSaveAsBookmarkSidePanelOpened =
        viewSidePanelContentId === ViewSidePanelContentIds.SAVE_BOOKMARK;

      // KFQ12026
      const isCanvasPrototype = featureOperations.hasFeature(
        featureContext,
        Features.CANVAS_PROTOTYPE
      );

      /**
       * Contributors that are part of the discover journey are not allowed to
       * change view settings, open the settings panel, or edit presentations.
       */
      const isUserOnDiscoverJourney = isDiscoverJourney();
      const isUserAllowedToChangeViewSetting = !isUserOnDiscoverJourney;
      const isUserAllowedToOpenSettings =
        !isUserOnDiscoverJourney && !hasNewSidebarAndNavbar;

      const toggleLegendButton = {
        id: 'toggleLegend',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.toggleLegend',
          'Toggle legend'
        ),
        iconName: IconName.INFO,
        type: SettingsType.TOGGLE,
        onClick: toggleLegend,
        isActive: isLegendActive,
        isDisabled: toggleLegend === noop,
      };

      const rightControlsLeftOptions = isUserAllowedToChangeViewSetting
        ? [
            {
              id: 'viewOptions',
              type: SettingsType.BUTTON,
              iconName: IconName.TUNE_SETTINGS,
              label: t(
                i18nLocale,
                'appLayout:viewPaneHeader.viewOptionsButtonLabel',
                'View style, options and settings'
              ),
              ariaLabel: t(
                i18nLocale,
                'appLayout:viewPaneHeader.viewSettingsAriaLabel',
                'view settings'
              ),
              isActive: isViewOptionsOpened,
              onClick: event => {
                event.stopPropagation();
                if (isViewOptionsOpened) {
                  dispatchAction(hideViewSidePanel());
                  return;
                }

                if (isCanvasPrototype) {
                  dispatchAction(
                    toggleSidebarPanel(SidebarPanelContentIds.VIEW_SETTINGS)
                  );
                } else {
                  dispatchAction(
                    showViewSidePanel(ViewSidePanelContentIds.VIEW_OPTIONS)
                  );
                }

                trackOpenViewSettingsPanel();
              },
            } satisfies ButtonConfig,
            toggleLegendButton satisfies ButtonConfig,
          ]
        : [toggleLegendButton satisfies ButtonConfig];

      const isViewConformant = isViewEligibleForNewAssets(
        viewpointModeViews.byIds[mainViewId]
      );

      const showRightControlOptions =
        isUserAllowedToOpenSettings && !isCanvasPrototype;

      const hamburgerButton = {
        id: 'hamburger',
        type: SettingsType.BUTTON,
        iconName: IconName.SETTINGS,
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.hamburgerButtonLabel',
          'Metamodel, workspace, component and reference'
        ),
        isActive: isRightEditorPaneOpen,
        onClick: () =>
          dispatchAction(
            isRightEditorPaneOpen
              ? hideRightPane()
              : showRightPane({ type: GetContentOptionsType.MENU })
          ),
      } satisfies ButtonConfig;

      const presentationToggleButton = {
        id: 'toggle-presentation',
        type: SettingsType.BUTTON,
        iconName: IconName.PRESENTATION,
        label: presentationEditorToggle.label,
        isActive: presentationEditorToggle.isOpen,
        onClick: presentationEditorToggle.onToggle,
        isDisabled: !isViewConformant,
      } satisfies ButtonConfig;

      const rightControlsRightOptions = showRightControlOptions
        ? [
            hamburgerButton,
            ...(presentationEditorToggle.canAccess
              ? [presentationToggleButton]
              : []),
          ]
        : [];

      const saveButtonOptions =
        hasNewSidebarAndNavbar || isCanvasPrototype
          ? []
          : toSaveDropdownOptions({
              canSaveViewpoint,
              canAddPresentationSlide: presentationEditorToggle.canAccess,
              isSaveAsViewpointSidePanelOpened,
              isSaveAsBookmarkSidePanelOpened,
              mainViewId,
              isViewpointMode: true,
              exportToPng,
              exportToSvg,
              addToDashboard: getCanSaveViewpointAsWidget({
                permissionContext,
                featureContext,
              })
                ? addToDashboard
                : null,
              isLegendActive,
              hasLucidchartFeature: featureOperations.hasFeature(
                featureContext,
                Features.LUCID_CHART_ENABLE
              ),
              hasPptxExportFeature: featureOperations.hasFeature(
                featureContext,
                Features.PRESENTATIONS_PPTX_EXPORT
              ),
              isViewConformant,
              isConvertedFromWorkspaceMode,
              saveAsViewpointButtonState,
              i18nLocale,
            });

      return {
        isRightEditorPaneOpen,
        exportToPng,
        viewContext,
        saveAsViewpointButtonState,
        isViewOptionsOpened,
        rightControlsLeftOptions,
        rightControlsRightOptions,
        saveButtonOptions,
        isFileExporting,
        isSaveAsViewpointSidePanelOpened,
        mainViewId,
        i18nLocale,
      };
    }
  )
);
